'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Plus, Download, Trash2, PencilLine, Check, X, Printer } from 'lucide-react';
import * as XLSX from 'xlsx';

import SummaryBar from './SummaryBar';
import TransactionTable from './TransactionTable';
import TransactionModal from './TransactionModal';
import DeleteConfirmModal from './DeleteConfirmModal';

// ─── Types ────────────────────────────────────────────────────────────────────
export type TransactionType = 'entrada' | 'saida';

export interface Transaction {
  id: string;
  data: string;
  descricao: string;
  tipo: TransactionType;
  valor: number;
  saldo: number;
  tipoOperacao?: string;
  numero?: string;
}

export interface TransactionFormValues {
  data: string;
  descricao: string;
  tipo: TransactionType;
  valor: string;
  tipoOperacao?: string;
  numero?: string;
}

export interface Sheet {
  id: string;
  name: string;
  transactions: Omit<Transaction, 'saldo'>[];
}

const STORAGE_KEY = 'cashflow_sheets';
const STORAGE_ACTIVE_KEY = 'cashflow_active_sheet';

// ─── Seed data ────────────────────────────────────────────────────────────────
const SEED_TRANSACTIONS_RAW: Omit<Transaction, 'saldo'>[] = [
  { id: 'txn-001', data: '2026-05-01', descricao: 'Saldo inicial de caixa', tipo: 'entrada', valor: 5000.00 },
  { id: 'txn-002', data: '2026-05-02', descricao: 'Pagamento cliente — NF 1042', tipo: 'entrada', valor: 1800.00 },
  { id: 'txn-003', data: '2026-05-03', descricao: 'Aluguel escritório maio/2026', tipo: 'saida', valor: 2200.00 },
  { id: 'txn-004', data: '2026-05-05', descricao: 'Venda produto — Pedido 8821', tipo: 'entrada', valor: 640.50 },
  { id: 'txn-005', data: '2026-05-06', descricao: 'Conta de energia elétrica', tipo: 'saida', valor: 318.90 },
  { id: 'txn-006', data: '2026-05-07', descricao: 'Pagamento fornecedor — Mat. escritório', tipo: 'saida', valor: 487.00 },
  { id: 'txn-007', data: '2026-05-08', descricao: 'Recebimento parcela — contrato 2024/07', tipo: 'entrada', valor: 3200.00 },
  { id: 'txn-008', data: '2026-05-09', descricao: 'Despesa com transporte e entregas', tipo: 'saida', valor: 155.40 },
  { id: 'txn-009', data: '2026-05-10', descricao: 'Serviços prestados — cliente Ramos', tipo: 'entrada', valor: 950.00 },
  { id: 'txn-010', data: '2026-05-12', descricao: 'Pagamento internet e telefone', tipo: 'saida', valor: 289.90 },
  { id: 'txn-011', data: '2026-05-13', descricao: 'Reembolso despesa viagem', tipo: 'entrada', valor: 420.00 },
  { id: 'txn-012', data: '2026-05-14', descricao: 'Material de limpeza e manutenção', tipo: 'saida', valor: 134.70 },
];

const INITIAL_SHEETS: Sheet[] = [
  { id: 'sheet-1', name: 'Folha A', transactions: [] },
  { id: 'sheet-2', name: 'Folha B', transactions: [] },
];

function loadSheets(): Sheet[] {
  if (typeof window === 'undefined') return INITIAL_SHEETS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Sheet[];
  } catch {
    // ignore
  }
  return INITIAL_SHEETS;
}

function loadActiveSheetId(sheets: Sheet[]): string {
  if (typeof window === 'undefined') return sheets[0]?.id ?? INITIAL_SHEETS[0].id;
  try {
    const raw = localStorage.getItem(STORAGE_ACTIVE_KEY);
    if (raw && sheets.find((s) => s.id === raw)) return raw;
  } catch {
    // ignore
  }
  return sheets[0]?.id ?? INITIAL_SHEETS[0].id;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function computeBalances(txns: Omit<Transaction, 'saldo'>[], initialBalance = 0): Transaction[] {
  let running = initialBalance;
  return txns.map((t) => {
    if (t.tipo === 'entrada') running += t.valor;
    else running -= t.valor;
    return { ...t, saldo: running };
  });
}

function sortByDate(txns: Omit<Transaction, 'saldo'>[]): Omit<Transaction, 'saldo'>[] {
  return [...txns].sort((a, b) => {
    if (a.data < b.data) return -1;
    if (a.data > b.data) return 1;
    return 0;
  });
}

function formatDateBR(isoDate: string): string {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
}

function formatCurrency(value: number): string {
  const negative = value < 0;
  const abs = Math.abs(value);
  const fixed = abs.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '-' : ''}${intFormatted},${decPart} €`;
}

let nextTxnId = SEED_TRANSACTIONS_RAW.length + 1;
function generateTxnId(): string {
  const id = `txn-${String(nextTxnId).padStart(3, '0')}`;
  nextTxnId++;
  return id;
}

let nextSheetId = INITIAL_SHEETS.length + 1;
function generateSheetId(): string {
  const id = `sheet-${nextSheetId}`;
  nextSheetId++;
  return id;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function CashFlowSheetClient() {
  const [sheets, setSheets] = useState<Sheet[]>(INITIAL_SHEETS);
  const [activeSheetId, setActiveSheetId] = useState<string>(INITIAL_SHEETS[0].id);

  // Rename state
  const [renamingSheetId, setRenamingSheetId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Delete sheet confirm
  const [deleteSheetId, setDeleteSheetId] = useState<string | null>(null);

  // Modal state
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingFadeId, setDeletingFadeId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isClearConfirm, setIsClearConfirm] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Month filter (empty array = all months, non-empty = selected months)
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);

  // Type filter: 'all' | 'entrada' | 'saida'
  const [typeFilter, setTypeFilter] = useState<'all' | 'entrada' | 'saida'>('all');

  // Search query — lifted here so it resets on sheet/month changes
  const [searchQuery, setSearchQuery] = useState('');

  // Captures the sheet that was active when an operation was initiated,
  // so that switching tabs before confirming never redirects the change.
  const operationSheetIdRef = useRef<string>(activeSheetId);

  // Excel export date range modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');

  // Active sheet
  const activeSheet = sheets.find((s) => s.id === activeSheetId) ?? sheets[0];
  const transactions: Transaction[] = computeBalances(sortByDate(activeSheet?.transactions ?? []));

  // Filtered transactions for display (by selected months)
  const displayTransactions: Transaction[] = selectedMonths.length === 0
    ? transactions
    : (() => {
        const allSorted = sortByDate(activeSheet?.transactions ?? []);
        const minMonth = Math.min(...selectedMonths);

        // Compute the balance of all transactions strictly before the first selected month
        const beforeFirst = allSorted.filter((t) => {
          if (!t.data) return false;
          const month = parseInt(t.data.split('-')[1], 10);
          return month < minMonth;
        });
        let initialBalance = 0;
        beforeFirst.forEach((t) => {
          if (t.tipo === 'entrada') initialBalance += t.valor;
          else initialBalance -= t.valor;
        });

        // Filter transactions belonging to any of the selected months
        const filtered = allSorted.filter((t) => {
          if (!t.data) return false;
          const month = parseInt(t.data.split('-')[1], 10);
          return selectedMonths.includes(month);
        });

        return computeBalances(filtered, initialBalance);
      })();

  // Apply type filter on top of month-filtered transactions
  const visibleTransactions: Transaction[] = typeFilter === 'all'
    ? displayTransactions
    : displayTransactions.filter((t) => t.tipo === typeFilter);

  // Totals
  const totalEntradas = visibleTransactions.reduce((sum, t) => (t.tipo === 'entrada' ? sum + t.valor : sum), 0);
  const totalSaidas = visibleTransactions.reduce((sum, t) => (t.tipo === 'saida' ? sum + t.valor : sum), 0);
  const saldoAtual = visibleTransactions.length > 0 ? visibleTransactions[visibleTransactions.length - 1].saldo : 0;

  // Persist sheets to localStorage whenever they change
  useEffect(() => {
    if (!isMounted) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sheets));
    } catch {
      // ignore
    }
  }, [sheets, isMounted]);

  // Persist active sheet id to localStorage whenever it changes
  useEffect(() => {
    if (!isMounted) return;
    try {
      localStorage.setItem(STORAGE_ACTIVE_KEY, activeSheetId);
    } catch {
      // ignore
    }
  }, [activeSheetId, isMounted]);

  useEffect(() => {
    // Load persisted data from localStorage after mount (client-only)
    const loadedSheets = loadSheets();
    const loadedActiveId = loadActiveSheetId(loadedSheets);
    setSheets(loadedSheets);
    setActiveSheetId(loadedActiveId);
    operationSheetIdRef.current = loadedActiveId;
    setIsMounted(true);
  }, []);

  // Focus rename input when it opens
  useEffect(() => {
    if (renamingSheetId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingSheetId]);

  // Open new transaction modal when "+" key is pressed
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === '+') {
        e.preventDefault();
        setEditingTransaction(null);
        setIsAddEditModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Sheet management ────────────────────────────────────────────────────────
  const handleAddSheet = useCallback(() => {
    const newSheet: Sheet = {
      id: generateSheetId(),
      name: `Folha ${String.fromCharCode(65 + sheets.length)}`,
      transactions: [],
    };
    setSheets((prev) => [...prev, newSheet]);
    setActiveSheetId(newSheet.id);
    setIsClearConfirm(false);
  }, [sheets.length]);

  const handleStartRename = useCallback((sheet: Sheet) => {
    setRenamingSheetId(sheet.id);
    setRenameValue(sheet.name);
  }, []);

  const handleConfirmRename = useCallback(() => {
    if (!renamingSheetId) return;
    const trimmed = renameValue.trim();
    if (trimmed) {
      setSheets((prev) =>
        prev.map((s) => (s.id === renamingSheetId ? { ...s, name: trimmed } : s))
      );
    }
    setRenamingSheetId(null);
    setRenameValue('');
  }, [renamingSheetId, renameValue]);

  const handleCancelRename = useCallback(() => {
    setRenamingSheetId(null);
    setRenameValue('');
  }, []);

  const handleDeleteSheet = useCallback((sheetId: string) => {
    setDeleteSheetId(sheetId);
  }, []);

  const handleConfirmDeleteSheet = useCallback(() => {
    if (!deleteSheetId) return;
    setSheets((prev) => {
      const remaining = prev.filter((s) => s.id !== deleteSheetId);
      if (activeSheetId === deleteSheetId && remaining.length > 0) {
        setActiveSheetId(remaining[0].id);
      }
      return remaining;
    });
    setDeleteSheetId(null);
    setIsClearConfirm(false);
  }, [deleteSheetId, activeSheetId]);

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const updateActiveTransactions = useCallback(
    (updater: (prev: Omit<Transaction, 'saldo'>[]) => Omit<Transaction, 'saldo'>[], sheetId?: string) => {
      const targetId = sheetId ?? activeSheetId;
      setSheets((prev) =>
        prev.map((s) =>
          s.id === targetId ? { ...s, transactions: updater(s.transactions) } : s
        )
      );
    },
    [activeSheetId]
  );

  const handleAdd = useCallback(() => {
    operationSheetIdRef.current = activeSheetId;
    setEditingTransaction(null);
    setIsAddEditModalOpen(true);
  }, [activeSheetId]);

  const handleEdit = useCallback((txn: Transaction) => {
    operationSheetIdRef.current = activeSheetId;
    setEditingTransaction(txn);
    setIsAddEditModalOpen(true);
  }, [activeSheetId]);

  const handleDeleteRequest = useCallback((id: string) => {
    operationSheetIdRef.current = activeSheetId;
    setDeletingId(id);
    setIsDeleteModalOpen(true);
  }, [activeSheetId]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deletingId) return;
    const targetSheetId = operationSheetIdRef.current;
    setDeletingFadeId(deletingId);
    setTimeout(() => {
      updateActiveTransactions((prev) => sortByDate(prev.filter((t) => t.id !== deletingId)), targetSheetId);
      setDeletingFadeId(null);
      setDeletingId(null);
    }, 280);
    setIsDeleteModalOpen(false);
  }, [deletingId, updateActiveTransactions]);

  const handleModalSubmit = useCallback(
    (values: TransactionFormValues) => {
      const valor = parseFloat(values.valor.replace(',', '.'));
      if (isNaN(valor) || valor <= 0) return;
      const targetSheetId = operationSheetIdRef.current;

      if (editingTransaction) {
        updateActiveTransactions((prev) => {
          const updated = prev.map((t) =>
            t.id === editingTransaction.id
              ? { ...t, data: values.data, descricao: values.descricao, tipo: values.tipo, valor, tipoOperacao: values.tipoOperacao, numero: values.numero }
              : t
          );
          return sortByDate(updated);
        }, targetSheetId);
      } else {
        const newTxn: Omit<Transaction, 'saldo'> = {
          id: generateTxnId(),
          data: values.data,
          descricao: values.descricao,
          tipo: values.tipo,
          valor,
          tipoOperacao: values.tipoOperacao,
          numero: values.numero,
        };
        updateActiveTransactions((prev) => sortByDate([...prev, newTxn]), targetSheetId);
      }
      setIsAddEditModalOpen(false);
      setEditingTransaction(null);
    },
    [editingTransaction, updateActiveTransactions]
  );

  const handleClearSheet = useCallback(() => {
    if (!isClearConfirm) {
      operationSheetIdRef.current = activeSheetId;
      setIsClearConfirm(true);
      return;
    }
    updateActiveTransactions(() => [], operationSheetIdRef.current);
    setIsClearConfirm(false);
  }, [isClearConfirm, updateActiveTransactions, activeSheetId]);

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    setIsExporting(true);
    try {
      const exportData = transactions.map((t) => {
        const parts = [t.tipoOperacao, t.numero, t.descricao].filter(Boolean);
        return {
          Data: formatDateBR(t.data),
          Descrição: parts.join(' '),
          Entrada: t.tipo === 'entrada' ? parseFloat(t.valor.toFixed(2)) : '',
          Saída: t.tipo === 'saida' ? parseFloat(t.valor.toFixed(2)) : '',
          Saldo: parseFloat(t.saldo.toFixed(2)),
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [{ wch: 12 }, { wch: 60 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, activeSheet?.name ?? 'Folha de Caixa');

      const today = new Date();
      const dateStr = `${String(today.getDate()).padStart(2, '0')}${String(today.getMonth() + 1).padStart(2, '0')}${today.getFullYear()}`;
      const safeName = (activeSheet?.name ?? 'folha').replace(/[^a-zA-Z0-9_-]/g, '_');
      XLSX.writeFile(wb, `folhacaixa_${safeName}_${dateStr}.xlsx`);
    } finally {
      setTimeout(() => setIsExporting(false), 800);
    }
  }, [transactions, activeSheet]);

  const handleExportWithDates = useCallback((dateFrom: string, dateTo: string) => {
    setIsExporting(true);
    try {
      const filtered = transactions.filter((t) => {
        if (!dateFrom && !dateTo) return true;
        const tDate = t.data;
        if (dateFrom && tDate < dateFrom) return false;
        if (dateTo && tDate > dateTo) return false;
        return true;
      });

      const exportData = filtered.map((t) => {
        const parts = [t.tipoOperacao, t.numero, t.descricao].filter(Boolean);
        return {
          Data: formatDateBR(t.data),
          Descrição: parts.join(' '),
          Entrada: t.tipo === 'entrada' ? parseFloat(t.valor.toFixed(2)) : '',
          Saída: t.tipo === 'saida' ? parseFloat(t.valor.toFixed(2)) : '',
          Saldo: parseFloat(t.saldo.toFixed(2)),
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [{ wch: 12 }, { wch: 60 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, activeSheet?.name ?? 'Folha de Caixa');

      const today = new Date();
      const dateStr = `${String(today.getDate()).padStart(2, '0')}${String(today.getMonth() + 1).padStart(2, '0')}${today.getFullYear()}`;
      const safeName = (activeSheet?.name ?? 'folha').replace(/[^a-zA-Z0-9_-]/g, '_');
      XLSX.writeFile(wb, `folhacaixa_${safeName}_${dateStr}.xlsx`);
    } finally {
      setTimeout(() => setIsExporting(false), 800);
    }
  }, [transactions, activeSheet]);

  const handleOpenExportModal = useCallback(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    setExportDateFrom(`${yyyy}-${mm}-01`);
    const lastDay = new Date(yyyy, now.getMonth() + 1, 0).getDate();
    setExportDateTo(`${yyyy}-${mm}-${String(lastDay).padStart(2, '0')}`);
    setShowExportModal(true);
  }, []);

  // ── Print ───────────────────────────────────────────────────────────────────
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printDateFrom, setPrintDateFrom] = useState('');
  const [printDateTo, setPrintDateTo] = useState('');

  const handlePrint = useCallback(
    (dateFrom: string, dateTo: string) => {
      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        alert('O browser bloqueou a janela de impressão. Por favor, permita popups para este site.');
        return;
      }

      // Apply search filter on top of visibleTransactions (already month+type filtered)
      const searchFiltered = searchQuery
        ? visibleTransactions.filter((t) => {
            // Split by "+" to support multiple terms (OR logic)
            const terms = searchQuery.split('+').map(s => s.trim().toLowerCase()).filter(Boolean);
            if (terms.length === 0) return true;
            return terms.some(q =>
              t.descricao.toLowerCase().includes(q) ||
              formatDateBR(t.data).includes(q) ||
              String(t.valor).includes(q) ||
              (t.tipoOperacao ?? '').toLowerCase().includes(q) ||
              (t.numero ?? '').toLowerCase().includes(q)
            );
          })
        : visibleTransactions;

      // Filter transactions by date range from searchFiltered (already type+month+search filtered)
      const filtered = searchFiltered.filter((t) => {
        if (!dateFrom && !dateTo) return true;
        const tDate = t.data; // format: YYYY-MM-DD
        if (dateFrom && tDate < dateFrom) return false;
        if (dateTo && tDate > dateTo) return false;
        return true;
      });

      // Recompute running balance for the filtered subset.
      // The initial balance is the sum of all transactions in the full (unfiltered)
      // list whose date is strictly before dateFrom — i.e., the balance at the end
      // of the day before the selected interval starts.
      let printInitialBalance = 0;
      const allSorted = sortByDate(activeSheet?.transactions ?? []);
      if (dateFrom) {
        allSorted.forEach((t) => {
          if (t.data < dateFrom) {
            if (t.tipo === 'entrada') printInitialBalance += t.valor;
            else printInitialBalance -= t.valor;
          }
        });
      } else if (filtered.length > 0) {
        // No dateFrom: use position of first filtered transaction in full list
        const firstFilteredId = filtered[0].id;
        const idxInAll = allSorted.findIndex((t) => t.id === firstFilteredId);
        if (idxInAll > 0) {
          allSorted.slice(0, idxInAll).forEach((t) => {
            if (t.tipo === 'entrada') printInitialBalance += t.valor;
            else printInitialBalance -= t.valor;
          });
        }
      }
      let runningBalance = printInitialBalance;
      const filteredWithSaldo = filtered.map((t) => {
        if (t.tipo === 'entrada') runningBalance += t.valor;
        else runningBalance -= t.valor;
        return { ...t, saldo: runningBalance };
      });

      // Recalculate totals for filtered set
      let filteredEntradas = 0;
      let filteredSaidas = 0;
      filteredWithSaldo.forEach((t) => {
        if (t.tipo === 'entrada') filteredEntradas += t.valor;
        else filteredSaidas += t.valor;
      });
      const filteredSaldo = filteredEntradas - filteredSaidas;

      const rows = filteredWithSaldo.map((t, idx) => {
        const descParts = [t.tipoOperacao, t.numero, t.descricao].filter(Boolean);
        const descCombined = descParts.join(' ');
        const isPrintFiltered = selectedMonths.length > 0 || typeFilter !== 'all' || searchQuery !== '';
        return `
      <tr style="background:${idx % 2 === 0 ? '#fff' : '#f9fafb'}">
        <td style="border:1px solid #d1d5db;padding:4px 6px">${formatDateBR(t.data)}</td>
        <td style="border:1px solid #d1d5db;padding:4px 6px">${descCombined}</td>
        <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;color:#16a34a;white-space:nowrap;width:80px">${t.tipo === 'entrada' ? formatCurrency(t.valor) : ''}</td>
        <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;color:#dc2626;white-space:nowrap;width:80px">${t.tipo === 'saida' ? formatCurrency(t.valor) : ''}</td>
        ${!isPrintFiltered ? `<td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;color:${t.saldo >= 0 ? '#16a34a' : '#dc2626'};font-weight:bold;white-space:nowrap;width:80px">${formatCurrency(t.saldo)}</td>` : ''}
      </tr>
    `;
      }).join('');

      const emptyRow = filtered.length === 0
        ? `<tr><td colspan="${selectedMonths.length > 0 || typeFilter !== 'all' || searchQuery !== '' ? 4 : 5}" style="border:1px solid #d1d5db;padding:12px 8px;text-align:center;color:#6b7280">Sem lançamentos no período selecionado</td></tr>`
        : '';

      const today = new Date();
      const dateStr = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;

      // Build period label
      let periodoLabel = 'Todos os lançamentos';
      if (dateFrom && dateTo) {
        const [fyyyy, fmm, fdd] = dateFrom.split('-');
        const [tyyyy, tmm, tdd] = dateTo.split('-');
        periodoLabel = `Período: ${fdd}/${fmm}/${fyyyy} a ${tdd}/${tmm}/${tyyyy}`;
      } else if (dateFrom) {
        const [fyyyy, fmm, fdd] = dateFrom.split('-');
        periodoLabel = `A partir de: ${fdd}/${fmm}/${fyyyy}`;
      } else if (dateTo) {
        const [tyyyy, tmm, tdd] = dateTo.split('-');
        periodoLabel = `Até: ${tdd}/${tmm}/${tyyyy}`;
      }

      printWindow.document.write(`<!DOCTYPE html>
<html lang="pt">
<head>
  <meta charset="UTF-8"/>
  <title>Folha de Caixa — ${activeSheet?.name ?? ''}</title>
  <style>
    @page { margin: 1.5cm; size: A4; }
    body { font-family: Arial, sans-serif; font-size: 10px; color: #000; margin: 0; padding: 0; }
    h1 { font-size: 15px; font-weight: bold; margin: 0 0 4px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 9px; }
    th { background: #f3f4f6; border: 1px solid #d1d5db; padding: 5px 6px; text-align: left; }
    th:nth-child(1) { width: 62px; }
    th:nth-child(3), th:nth-child(4), th:nth-child(5) { text-align: right; width: 80px; white-space: nowrap; }
  </style>
</head>
<body>
  <div style="margin-bottom:16px;border-bottom:2px solid #000;padding-bottom:8px">
    <h1 className="text-2xl font-semibold text-foreground tracking-tight">
      Folha de caixa - Modelo Zezereconta, Lda
    </h1>
    <p style="margin:2px 0 0 0;font-size:11px;color:#555">${periodoLabel}</p>
    <p style="margin:2px 0 0 0;font-size:11px;color:#555">Impresso em ${dateStr}</p>
  </div>
  <div style="display:flex;gap:24px;margin-bottom:16px">
    <div><strong>Total Entradas:</strong> <span style="color:#16a34a">${formatCurrency(filteredEntradas)}</span></div>
    <div><strong>Total Saídas:</strong> <span style="color:#dc2626">${formatCurrency(filteredSaidas)}</span></div>
    ${!(selectedMonths.length > 0 || typeFilter !== 'all' || searchQuery !== '') ? `<div><strong>Saldo do Período:</strong> <span style="color:${filteredSaldo >= 0 ? '#16a34a' : '#dc2626'}">${formatCurrency(filteredSaldo)}</span></div>` : ''}
  </div>
  <table>
    <thead>
      <tr>
        <th>Data</th>
        <th>Descrição</th>
        <th style="text-align:right">Entrada</th>
        <th style="text-align:right">Saída</th>
        ${!(selectedMonths.length > 0 || typeFilter !== 'all' || searchQuery !== '') ? '<th style="text-align:right">Saldo</th>' : ''}
      </tr>
    </thead>
    <tbody>${rows}${emptyRow}</tbody>
  </table>
</body>
</html>`);

      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);
    },
    [visibleTransactions, searchQuery, selectedMonths, typeFilter, activeSheet, formatCurrency, formatDateBR]
  );

  const handleOpenPrintModal = useCallback(() => {
    if (selectedMonths.length > 0) {
      // Derive date range from the selected months
      // Use the year from the first visible transaction, or current year as fallback
      const firstTxn = visibleTransactions[0];
      const year = firstTxn ? parseInt(firstTxn.data.split('-')[0], 10) : new Date().getFullYear();

      const minMonth = Math.min(...selectedMonths);
      const maxMonth = Math.max(...selectedMonths);

      const fromMM = String(minMonth).padStart(2, '0');
      const toMM = String(maxMonth).padStart(2, '0');
      const lastDay = new Date(year, maxMonth, 0).getDate();

      setPrintDateFrom(`${year}-${fromMM}-01`);
      setPrintDateTo(`${year}-${toMM}-${String(lastDay).padStart(2, '0')}`);
    } else {
      // Default: current month
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      setPrintDateFrom(`${yyyy}-${mm}-01`);
      const lastDay = new Date(yyyy, now.getMonth() + 1, 0).getDate();
      setPrintDateTo(`${yyyy}-${mm}-${String(lastDay).padStart(2, '0')}`);
    }
    setShowPrintModal(true);
  }, [selectedMonths, visibleTransactions]);

  return (
    <div className="flex flex-col gap-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            Folha de caixa - Modelo Zezereconta, Lda
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Registre entradas e saídas — saldo calculado automaticamente
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isClearConfirm && (
            <button
              onClick={() => setIsClearConfirm(false)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-card text-muted-foreground text-sm font-medium hover:bg-muted transition-all duration-150 active:scale-95"
            >
              Cancelar
            </button>
          )}
          <button
            onClick={handleClearSheet}
            disabled={isMounted && (transactions.length === 0 && !isClearConfirm)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
              isClearConfirm
                ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-primary text-primary-foreground hover:opacity-90 shadow-sm'
            }`}
          >
            {isClearConfirm ? 'Confirmar limpeza' : 'Limpar Folha'}
          </button>
          <button
            onClick={handleOpenExportModal}
            disabled={isMounted && (isExporting || transactions.length === 0)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            <Download size={16} />
            {isExporting ? 'Exportando…' : 'Exportar Excel'}
          </button>
          <button
            onClick={handleOpenPrintModal}
            disabled={isMounted && transactions.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            <Printer size={16} />
            Imprimir
          </button>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all duration-150 active:scale-95 shadow-sm"
          >
            <Plus size={16} />
            Novo Lançamento
          </button>
        </div>
      </div>

      {/* Sheet tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto pb-0 print:hidden">
        {sheets.map((sheet) => (
          <div
            key={sheet.id}
            className={`group relative flex items-center gap-1 px-3 py-2 rounded-t-md border-b-2 cursor-pointer select-none transition-all duration-150 whitespace-nowrap ${
              sheet.id === activeSheetId
                ? 'border-primary bg-primary/5 text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            onClick={() => {
              if (renamingSheetId !== sheet.id) {
                setActiveSheetId(sheet.id);
                setIsClearConfirm(false);
                setSelectedMonths([]);
                setTypeFilter('all');
                setSearchQuery('');
              }
            }}
          >
            {renamingSheetId === sheet.id ? (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConfirmRename();
                    if (e.key === 'Escape') handleCancelRename();
                  }}
                  className="w-28 text-sm border border-primary rounded px-1.5 py-0.5 bg-background text-foreground outline-none focus:ring-1 focus:ring-primary"
                  maxLength={30}
                />
                <button
                  onClick={handleConfirmRename}
                  className="text-green-600 hover:text-green-700 p-0.5"
                  title="Confirmar"
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={handleCancelRename}
                  className="text-muted-foreground hover:text-foreground p-0.5"
                  title="Cancelar"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <span
                  className="text-sm"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    handleStartRename(sheet);
                  }}
                  title="Duplo clique para renomear"
                >
                  {sheet.name}
                </span>
                {/* Actions shown on hover for active tab */}
                {sheet.id === activeSheetId && (
                  <div className="flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartRename(sheet);
                      }}
                      className="p-0.5 rounded hover:bg-primary/10 text-primary/60 hover:text-primary"
                      title="Renomear folha"
                    >
                      <PencilLine size={12} />
                    </button>
                    {sheets.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSheet(sheet.id);
                        }}
                        className="p-0.5 rounded hover:bg-red-100 text-muted-foreground hover:text-red-600"
                        title="Eliminar folha"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        {/* Add new sheet button */}
        <button
          onClick={handleAddSheet}
          className="flex items-center gap-1 px-3 py-2 rounded-t-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 border-b-2 border-transparent whitespace-nowrap"
          title="Nova folha"
        >
          <Plus size={14} />
          Nova Folha
        </button>
      </div>

      {/* Month filter buttons */}
      <div className="flex items-center gap-1 flex-wrap print:hidden">
        <button
          onClick={() => { setSelectedMonths([]); setSearchQuery(''); }}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 active:scale-95 ${
            selectedMonths.length === 0
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
          }`}
        >
          Todos
        </button>
        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
          <button
            key={m}
            onClick={() => {
              setSelectedMonths((prev) =>
                prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
              );
              setSearchQuery('');
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 active:scale-95 min-w-[2.25rem] ${
              selectedMonths.includes(m)
                ? 'bg-green-600 text-white shadow-sm'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      {/* Type filter buttons */}
      <div className="flex items-center gap-1 flex-wrap print:hidden">
        <span className="text-xs text-muted-foreground mr-1">Tipo:</span>
        {(['all', 'entrada', 'saida'] as const).map((type) => (
          <button
            key={type}
            onClick={() => { setTypeFilter(type); setSearchQuery(''); }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 active:scale-95 ${
              typeFilter === type
                ? type === 'entrada' ?'bg-green-600 text-white shadow-sm'
                  : type === 'saida' ?'bg-red-500 text-white shadow-sm' :'bg-primary text-primary-foreground shadow-sm' :'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
            }`}
          >
            {type === 'all' ? 'Todos' : type === 'entrada' ? 'Entradas' : 'Saídas'}
          </button>
        ))}
      </div>

      {/* Summary bar */}
      <SummaryBar
        totalEntradas={totalEntradas}
        totalSaidas={totalSaidas}
        saldoAtual={saldoAtual}
        totalTransacoes={visibleTransactions.length}
        formatCurrency={formatCurrency}
      />

      {/* Table */}
      <TransactionTable
        transactions={visibleTransactions}
        deletingFadeId={deletingFadeId}
        onEdit={handleEdit}
        onDelete={handleDeleteRequest}
        formatDateBR={formatDateBR}
        formatCurrency={formatCurrency}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isFiltered={selectedMonths.length > 0 || typeFilter !== 'all' || searchQuery !== ''}
      />

      {/* Add / Edit modal */}
      {isAddEditModalOpen && (
        <TransactionModal
          transaction={editingTransaction}
          onClose={() => {
            setIsAddEditModalOpen(false);
            setEditingTransaction(null);
          }}
          onSubmit={handleModalSubmit}
        />
      )}

      {/* Delete transaction modal */}
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          onClose={() => {
            setIsDeleteModalOpen(false);
            setDeletingId(null);
          }}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Delete sheet confirm modal */}
      {deleteSheetId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-base font-semibold text-foreground mb-2">Eliminar folha</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Tem a certeza que quer eliminar a folha{' '}
              <span className="font-medium text-foreground">
                &quot;{sheets.find((s) => s.id === deleteSheetId)?.name}&quot;
              </span>
              ? Todos os lançamentos serão apagados.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteSheetId(null)}
                className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDeleteSheet}
                className="px-4 py-2 rounded-md bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-all"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Date Range Modal */}
      {showPrintModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Printer size={18} />
                Imprimir Folha de Caixa
              </h2>
              <button
                onClick={() => setShowPrintModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Selecione o intervalo de datas a imprimir. Deixe em branco para imprimir todos os lançamentos.
            </p>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Data de Início
                </label>
                <input
                  type="date"
                  value={printDateFrom}
                  onChange={(e) => setPrintDateFrom(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Data de Fim
                </label>
                <input
                  type="date"
                  value={printDateTo}
                  onChange={(e) => setPrintDateTo(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPrintModal(false)}
                className="flex-1 px-4 py-2 rounded-md border border-border bg-card text-foreground text-sm font-medium hover:bg-muted transition-all duration-150"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowPrintModal(false);
                  handlePrint(printDateFrom, printDateTo);
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all duration-150 active:scale-95"
              >
                <Printer size={15} />
                Imprimir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Export Date Range Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Download size={18} />
                Exportar Excel
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Selecione o intervalo de datas a exportar. Deixe em branco para exportar todos os lançamentos.
            </p>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Data de Início
                </label>
                <input
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Data de Fim
                </label>
                <input
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="flex-1 px-4 py-2 rounded-md border border-border bg-card text-foreground text-sm font-medium hover:bg-muted transition-all duration-150"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowExportModal(false);
                  handleExportWithDates(exportDateFrom, exportDateTo);
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all duration-150 active:scale-95"
              >
                <Download size={15} />
                Exportar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating add button */}
      <button
        onClick={handleAdd}
        title="Novo Lançamento"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:opacity-90 active:scale-95 transition-all duration-150"
      >
        <Plus size={28} />
      </button>
    </div>
  );
}